import { App } from 'App'
import { isEqual } from 'es-toolkit'
import { createRoot } from 'react-dom/client'

import { getCurrentPage } from '@subaio/components'
import { apiCreate, delay, initErrorClient } from '@subaio/utils'
import { ContainerStatus, getRoutes } from '@subaio/webapp/src/containers'
import { getContainers } from '@subaio/webapp/src/containers/containers'

import { config } from 'clientConfig'

import { billingAuth } from 'lib/billingAuth'
import type { Container } from 'lib/container'
import { createRouter } from 'lib/router'
import { storeCreate } from 'lib/store'
import { getUserAuth } from 'lib/userAuth'

initErrorClient(
	{
		enabled: true,
		dataSourceName: config.sentry.dataSourceName,
		release: config.version,
		environment: config.environment,
	},
	() => {
		const {
			page: { name, params, query },
		} = store.getState()

		return {
			user: undefined,
			view: { name, params, query },
		}
	},
)

const store = storeCreate()

if (billingAuth.restoreDesign() || /^\/(?:personal\/)?billing\/?$/u.test(window.location.pathname)) {
	const userAuth = getUserAuth(billingAuth.restore())
	if (userAuth.status === 'authenticated') store.set({ userAuth })
	else billingAuth.clear()
}

if (config.isDev) {
	;(window as any).store = store
}

const api = apiCreate({
	baseUrl: '/api',
	logRequestData: config.isDev,
	getToken: () => {
		const { userAuth } = store.getState()
		return userAuth.status === 'authenticated' ? userAuth.user.token : null
	},
	getLocation: () => store.getState().page.name,
	shouldRetry: async (error, attempts) => {
		// If the request failed because of a network problem, retry (max 3 times)
		if (error.request.statusCode === 0) {
			// Wait one second longer after each attempt
			await delay(attempts)
			return true
		}

		return false
	},
})

const root = createRoot(document.getElementById('root')!)

const render = () => {
	const containers = getContainers<Container>()
	const router = createRouter(getRoutes(containers), () => store.getState().page)
	const currentPage = getCurrentPage(router)
	const { page, status } = store.getState()
	if (currentPage && (!isEqual(page, currentPage) || status === ContainerStatus.Errored)) {
		store.set({ page: currentPage, status: ContainerStatus.Initializing, data: {} })
	}
	// Render app
	root.render(
		<App
			containers={containers}
			store={store}
			router={router}
			api={api}
			setUser={(options) => {
				const userAuth = getUserAuth(options)
				if (userAuth.status === 'authenticated') billingAuth.save(userAuth.user)
				else billingAuth.clear()
				store.set({ userAuth })
			}}
		/>,
	)
}

render()

// Hot Module Replacement API
if (module.hot) {
	module.hot.accept(['App', '@subaio/webapp/src/containers/containers'], () => {
		render()
	})
}
